/**
 * Defined route names for consistency between route definitions and programmatic navigation.
 *
 * NOTE: Some routes may require parameters, and should instead use `routeLink` for programmatic navigation!
 *
 * NOTE: Not all routes must be named safely, but rather only when their name is used programmatically!
 *         Additionally, ensure route names are used with `{name: routeName}` syntax to avoid pathing issues!
 */

// NOTE: ⚠️ Route names **must** be unique for optimal safety/consistency when navigating!
export const routeNames = {
  auth: {
    login: "authLogin",
    logout: "authLogout",
    profile: "authProfile",
  },
  composer: {
    dashboard: "composerDashboard",
    submission: {
      index: "composerSubmissions",
      add: "composerSubmissionAdd",
      /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
      edit: "composerSubmissionEdit",
      /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
      detail: "composerSubmissionDetail",
      /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
      review: "composerSubmissionReview",
    },
  },
  collaborator: {
    composer: {
      index: "collaboratorComposers",
      /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
      detail: "collaboratorComposerDetail",
    },
    dashboard: "collaboratorDashboard",
    submission: {
      index: "collaboratorSubmissions",
      /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
      detail: "collaboratorSubmissionDetail",
      /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
      feedback: "collaboratorSubmissionFeedback",
      /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
      review: "collaboratorSubmissionReview",
    },
    season: "collaboratorSeasons",
    settings: "collaboratorSettings",
    team: "collaboratorTeam",
  },
} as const;

/**
 * Defined router links for consistency between route definitions and programmatic navigation.
 *
 * Preferred over `routeNames` when parameters are required, as it allows for dynamic URL generation.
 */
export const routeLinks = {
  composer: {
    submission: {
      edit: (id: string) => ({ name: routeNames.composer.submission.edit, params: { id } }),
      detail: (id: string) => ({ name: routeNames.composer.submission.detail, params: { id } }),
      review: (id: string) => ({ name: routeNames.composer.submission.review, params: { id } }),
    },
  },
  collaborator: {
    composer: {
      detail: (id: string) => ({ name: routeNames.collaborator.composer.detail, params: { id } }),
    },
    submission: {
      detail: (id: string) => ({ name: routeNames.collaborator.submission.detail, params: { id } }),
      feedback: (id: string) => ({
        name: routeNames.collaborator.submission.feedback,
        params: { id },
      }),
      review: (id: string) => ({ name: routeNames.collaborator.submission.review, params: { id } }),
    },
  },
} as const;
